import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import type { SalaDaLista } from "./ListaDeSalas";

const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/** A sala de cada profissional em cada dia da semana — só a sugestão ao marcar a consulta (dá pra
 *  trocar na hora). Dia fora do expediente da clínica aparece apagado. */
export function SalasDosProfissionais({ salas }: { salas: SalaDaLista[] }) {
  const utils = trpc.useUtils();
  const dentistas = (trpc.organization.listDentists.useQuery({ apenasAtivos: true }).data ?? []) as { id: string; name: string }[];
  const agenda = trpc.settings.getAgenda.useQuery().data;
  const salvar = trpc.salas.salvarSalasPadrao.useMutation({
    onSuccess: () => utils.settings.getAgenda.invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const ativas = salas.filter((s) => s.ativa);
  if (!agenda || !dentistas.length || !ativas.length) return null;

  const porDia = (dentistId: string): Record<string, string> => agenda.dentistSettings?.[dentistId]?.salaPorDia ?? {};
  const mudar = (dentistId: string, dia: number, salaId: string) => {
    const atual = { ...porDia(dentistId) };
    if (salaId) atual[String(dia)] = salaId;
    else delete atual[String(dia)];
    salvar.mutate({ dentistId, salaPorDia: atual });
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-border">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-zinc-100 bg-zinc-50/60 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:border-border dark:bg-muted/30">
            <th className="px-3 py-2 text-left">Profissional</th>
            {DIAS.map((d, i) => (
              <th key={d} className={cn("px-1 py-2 text-center", !agenda.workingDays.includes(i) && "opacity-40")}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-border/50">
          {dentistas.map((d) => (
            <tr key={d.id}>
              <td className="whitespace-nowrap px-3 py-2 font-medium text-zinc-800 dark:text-foreground">{d.name}</td>
              {DIAS.map((dia, i) => {
                const valor = porDia(d.id)[String(i)] ?? "";
                const sala = ativas.find((s) => s.id === valor);
                return (
                  <td key={dia} className={cn("px-1 py-1.5", !agenda.workingDays.includes(i) && "opacity-40")}>
                    <select
                      value={sala ? valor : ""}
                      onChange={(e) => mudar(d.id, i, e.target.value)}
                      aria-label={`Sala de ${d.name} na ${dia}`}
                      className="h-8 w-full rounded-md border border-zinc-200 bg-white px-1 text-[12px] dark:border-border dark:bg-card"
                      style={sala?.cor ? { borderColor: sala.cor } : undefined}
                    >
                      <option value="">—</option>
                      {ativas.map((s) => (
                        <option key={s.id} value={s.id}>{s.nome}</option>
                      ))}
                    </select>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
